import { motion, useScroll, useTransform, useMotionValue, useInView, animate } from "framer-motion";
import { useRef, useEffect } from "react";
import { Check } from "lucide-react";

interface StepVisualProps {
  autoplay?: boolean;
}

const DeconstructStep = ({ autoplay = false }: StepVisualProps = {}) => {
  const targetRef = useRef(null);
  const { scrollYProgress: scrollProg } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"]
  });
  // In the accordion the card is driven by a timed play (gated on in-view) so
  // its chaos→grid animation runs each time the row is opened / scrolled to.
  const autoMV = useMotionValue(0);
  const inView = useInView(targetRef, { amount: 0.4 });
  useEffect(() => {
    if (!autoplay) return;
    if (inView) {
      const controls = animate(autoMV, 1, { duration: 1.6, ease: "easeInOut", delay: 0.1 });
      return () => controls.stop();
    }
    autoMV.set(0);
  }, [autoplay, inView, autoMV]);
  const scrollYProgress = autoplay ? autoMV : scrollProg;

  // Calculate perfect grid geometry:
  // Card base dimensions: 128x160. Final scale is 0.75 (96x120px).
  // Desired uniform visual gap: 20px.
  // Delta X = 96 + 20 = 116px
  // Delta Y = 120 + 20 = 140px
  
  const col1 = -174;
  const col2 = -58;
  const col3 = 58;
  const col4 = 174;

  const row1 = -140;
  const row2 = 0;
  const row3 = 140;

  const items = [
    // Row 1
    { img: "/deconstruct-1.png", chaos: { x: -180, y: -120, r: -25 }, grid: { x: col1, y: row1 } },
    { img: "/deconstruct-2.png", chaos: { x: 200, y: -100, r: 30 }, grid: { x: col2, y: row1 } },
    { img: "/deconstruct-3.png", chaos: { x: -150, y: 140, r: -15 }, grid: { x: col3, y: row1 } },
    { img: "/deconstruct-4.png", chaos: { x: 160, y: -140, r: 35 }, grid: { x: col4, y: row1 } },
    
    // Row 2
    { img: "/deconstruct-5.png", chaos: { x: -220, y: 80, r: -40 }, grid: { x: col1, y: row2 } },
    { img: "/deconstruct-6.png", chaos: { x: 100, y: 160, r: 20 }, grid: { x: col2, y: row2 } },
    { img: "/deconstruct-7.png", chaos: { x: -90, y: -160, r: -30 }, grid: { x: col3, y: row2 } },
    { img: "/deconstruct-8.png", chaos: { x: 190, y: 100, r: 45 }, grid: { x: col4, y: row2 } },
    
    // Row 3
    { img: "/deconstruct-9.png", chaos: { x: -140, y: -80, r: -20 }, grid: { x: col1, y: row3 } },
    { img: "/deconstruct-10.png", chaos: { x: 140, y: 180, r: 25 }, grid: { x: col2, y: row3 } },
    { img: "/deconstruct-11.png", chaos: { x: -200, y: 120, r: -35 }, grid: { x: col3, y: row3 } },
    { img: "/deconstruct-12.png", chaos: { x: 170, y: -120, r: 40 }, grid: { x: col4, y: row3 } }
  ];

  return (
    <div ref={targetRef} className="relative w-full h-[400px] sm:h-[500px] bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-white" />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />

      <div className="relative h-full w-full flex items-center justify-center perspective-1000">
        {items.map((item, i) => (
          <motion.div 
            key={i} 
            className="absolute w-24 h-32 sm:w-32 sm:h-40 bg-white shadow-md rounded-sm overflow-hidden border border-slate-200" 
            style={{
              top: "50%",
              left: "50%",
              x: useTransform(scrollYProgress, [0.0, 0.25, 0.5], [0, item.chaos.x * 0.8, item.grid.x]),
              y: useTransform(scrollYProgress, [0.0, 0.25, 0.5], [0, item.chaos.y * 0.8, item.grid.y]),
              rotate: useTransform(scrollYProgress, [0.0, 0.25, 0.5], [0, item.chaos.r, 0]),
              scale: useTransform(scrollYProgress, [0.0, 0.25, 0.5], [0.5, 0.8, 0.75]),
              opacity: useTransform(scrollYProgress, [0, 0.1], [0, 1]),
              zIndex: items.length - i,
              marginTop: -64,
              marginLeft: -48
            }}
          >
            <div className="w-full h-full bg-white rounded-sm overflow-hidden relative group">
              <img src={item.img} alt="Clothing Item" className="w-full h-full object-cover mix-blend-multiply" />
            </div>
          </motion.div>
        ))}

        {/* Enhanced "Organized" Badge */}
        <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-3 px-5 py-3 bg-[#0f172a]/95 backdrop-blur-md border border-slate-700 rounded-md shadow-[0_20px_40px_rgba(0,0,0,0.4)] z-30 whitespace-nowrap" style={{
          opacity: useTransform(scrollYProgress, [0.4, 0.5], [0, 1]),
          scale: useTransform(scrollYProgress, [0.4, 0.5], [0.8, 1])
        }}>
          <div className="w-6 h-6 rounded-sm bg-[#c2410c] flex items-center justify-center text-white shadow-md">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
          </div>
          <div className="flex flex-col">
            <span className="text-[8px] font-bold text-[#c2410c] tracking-[0.2em] uppercase leading-none mb-1">Status</span>
            <span className="text-[12px] font-bold text-white leading-none tracking-widest uppercase">Digitized</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DeconstructStep;